/**
 * Merge parent indicator rows with a virtual Arrow dim window at render time (no Redux children).
 */

import {
  BUILDER_ARROW_ROW_HEIGHT,
  BUILDER_ARROW_WINDOW_SIZE,
  buildArrowVirtualSpacerRow,
  buildDimChildRowsFromArrowTable,
  inferPeriodColumns,
} from './builderArrowDetailRows';
import { getBuilderArrowDimWindow } from './builderArrowDetailWindowStore';

import type { Table } from 'apache-arrow';

type TableSourceRow = {
  id?: number | string;
  name?: string;
  chevronType?: string;
  type?: string;
  data_type?: string;
  spacerHeightPx?: number;
  parentId?: number;
};

/**
 * Insert spacers + visible window rows under each expanded parent.
 */
export function mergeArrowVirtualDimTableData(
  parentRows: TableSourceRow[],
  expandedParentIds: ReadonlySet<number>,
  detailTable: Table | undefined,
  detailMeta: { indicatorColumn: string; dimColumns: string[] } | undefined,
): TableSourceRow[] {
  if (!detailTable || !detailMeta?.dimColumns?.length) {
    return parentRows;
  }

  const merged: TableSourceRow[] = [];

  parentRows.forEach((row) => {
    if (row?.chevronType === 'child') {
      return;
    }

    merged.push(row);

    const parentId = Number(row?.id);
    if (!expandedParentIds.has(parentId) || row?.chevronType !== 'parent') {
      return;
    }

    const windowState = getBuilderArrowDimWindow(parentId);
    if (!windowState || windowState.totalCount <= 0) {
      return;
    }

    const periodColumns = inferPeriodColumns(
      detailTable,
      detailMeta.indicatorColumn,
      detailMeta.dimColumns,
    );

    const { startIndex, indicatorName, totalCount } = windowState;
    const { type, data_type: dataTypeLegacy } = row;
    const windowRows = buildDimChildRowsFromArrowTable(detailTable, {
      indicatorColumn: detailMeta.indicatorColumn,
      indicatorName,
      indicatorId: parentId,
      dataType: type ?? dataTypeLegacy,
      dimColumns: detailMeta.dimColumns,
      periodColumns,
      matchSkip: startIndex,
      maxRows: BUILDER_ARROW_WINDOW_SIZE,
    });

    const topSpacerPx = startIndex * BUILDER_ARROW_ROW_HEIGHT;
    const bottomCount = Math.max(0, totalCount - startIndex - windowRows.length);
    const bottomSpacerPx = bottomCount * BUILDER_ARROW_ROW_HEIGHT;

    if (topSpacerPx > 0) {
      merged.push(buildArrowVirtualSpacerRow(parentId, 'top', topSpacerPx));
    }
    merged.push(...windowRows);
    if (bottomSpacerPx > 0) {
      merged.push(buildArrowVirtualSpacerRow(parentId, 'bottom', bottomSpacerPx));
    }
  });

  return merged;
}

/**
 * Parent ids in table order (for scroll segment mapping).
 */
export function listParentIndicatorIds(parentRows: TableSourceRow[]): number[] {
  return parentRows
    .filter((row) => row?.chevronType !== 'child' && row?.chevronType !== 'arrow-spacer')
    .map((row) => Number(row?.id))
    .filter((id) => !Number.isNaN(id));
}
